// Shared quote status label (Session AQW, Q-5). Before this, the office list said "Awaiting
// customer" for `pending` (QuotesPage.tsx's own STATUS_WORD) and the dashboard said "Awaiting
// signature" for the SAME status (ActiveQuotesPanel.tsx's own hand-rolled QuoteStatusBadge) --
// two different words for one status, and neither belonged where it was. "Awaiting signature" is
// the accurate phrase for `accepted` (the customer HAS chosen a tier; the ESA is with Signable and
// only a signature is missing) -- it was never a Signable state at all when applied to `pending`.
// One label per status, read everywhere a quote's status reaches an office screen.
//
// `sent` and `opened` are QuoteStatus enum values that no live route writes any more (confirmed by
// a full source grep, Session AQW): both are legacy Master Sheet import artefacts, and every live
// route already treats `pending`/`sent` as an undifferentiated pair. They read the same label here
// rather than inventing a distinction nothing else in the platform makes.

export type QuoteStatusValue =
  | "draft"
  | "pending"
  | "sent"
  | "accepted"
  | "signed"
  | "voided"
  | "expired"
  | "opened"
  | "declined"
  | "lost";

const LABEL: Record<QuoteStatusValue, string> = {
  draft: "Draft",
  pending: "Awaiting customer",
  sent: "Awaiting customer",
  opened: "Awaiting customer",
  accepted: "Awaiting signature",
  signed: "Signed",
  voided: "Voided",
  expired: "Expired",
  declined: "Declined",
  lost: "Lost",
};

export function quoteStatusLabel(status: string): string {
  return LABEL[status as QuoteStatusValue] ?? status;
}

// Session ARP: the ONE tone per status, read by the Quote Hub's header pill and every Quotes list row
// and card (the Director's 25 September 2026 ruling: brand colours only on a quote row). It feeds the
// shared StatusPill `tone`, whose per-tone table holds the colours, so no quote screen carries a hex
// of its own. Moved verbatim from QuoteHubPage.tsx, which chose these tones in Session AQW.
export type QuoteStatusTone = "green" | "blue" | "amber" | "red" | "purple" | "grey";

export function quoteStatusTone(status: string): QuoteStatusTone {
  if (status === "signed") return "green";
  if (status === "accepted") return "amber";
  if (status === "lost" || status === "declined") return "red";
  if (status === "voided" || status === "expired") return "grey";
  if (status === "draft") return "purple";
  return "blue"; // pending / sent / opened -- all read as "awaiting customer"
}
